import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, teamLeaderNavItems, workerNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Users,
  User,
  DollarSign,
  Clock,
  CheckCircle2,
  MessageSquare,
  Send,
  AlertTriangle,
  Shield,
  HardHat,
  FileText,
  Info,
  FileSignature,
  Camera,
  Image,
  TrendingUp,
  Upload,
  X,
  Building2,
  Phone,
  Signature,
  PenLine,
  Eye,
  Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const chatEndRef = useRef(null);
  
  // Get initial tab from URL params
  const initialTab = searchParams.get("tab") || "details";
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [teamContract, setTeamContract] = useState(null);
  const [dailyProgress, setDailyProgress] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [application, setApplication] = useState(null);
  const [memberShares, setMemberShares] = useState([]);
  const [sharePerMember, setSharePerMember] = useState(0);
  
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Chat
  const [chatMessage, setChatMessage] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  
  // Daily Progress Form
  const [progressDescription, setProgressDescription] = useState("");
  const [progressHours, setProgressHours] = useState("");
  const [progressNotes, setProgressNotes] = useState("");
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submittingProgress, setSubmittingProgress] = useState(false);
  
  // Contract signing dialog
  const [signingContract, setSigningContract] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signatureText, setSignatureText] = useState("");
  const [sjaAcknowledged, setSjaAcknowledged] = useState({
    readHazards: false,
    understandPPE: false,
    waitingTime: false,
    acceptTerms: false
  });
  const [hazardAcknowledged, setHazardAcknowledged] = useState({});
  const [cedePercentage, setCedePercentage] = useState(0);
  
  // Digital signature pad
  const [signaturePadOpen, setSignaturePadOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const canvasRef = useRef(null);
  
  // Completion signing state
  const [completionStatus, setCompletionStatus] = useState(null);
  const [showCompletionSignModal, setShowCompletionSignModal] = useState(false);
  const [signingCompletion, setSigningCompletion] = useState(false);
  const completionCanvasRef = useRef(null);
  const [hasCompletionSignature, setHasCompletionSignature] = useState(false);
  const [isDrawingCompletion, setIsDrawingCompletion] = useState(false);
  
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;

  // Auto-fill signature text with user name when dialog opens
  useEffect(() => {
    if (signDialogOpen && user?.name) {
      setSignatureText(user.name);
    }
  }, [signDialogOpen, user]);

  // Reset checkboxes when dialog opens
  useEffect(() => {
    if (signDialogOpen) {
      setSjaAcknowledged({ readHazards: false, understandPPE: false, waitingTime: false, acceptTerms: false });
      // Reset hazard acknowledgements
      const hazards = project?.sja_form?.hazards || [];
      const initialHazardAck = {};
      hazards.forEach((_, idx) => { initialHazardAck[idx] = false; });
      setHazardAcknowledged(initialHazardAck);
    }
  }, [signDialogOpen, project]);
  
  // Setup canvas when signature pad opens
  useEffect(() => {
    if (signaturePadOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      setHasSignature(false);
    }
  }, [signaturePadOpen]);

  // Drawing functions for signature pad
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleConfirmSignature = async () => {
    // Get signature image data
    const canvas = canvasRef.current;
    const signatureImage = canvas.toDataURL('image/png');
    
    // Close signature pad and proceed with contract signing
    setSignaturePadOpen(false);
    await handleSignContractWithSignature(signatureImage);
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  useEffect(() => {
    // Poll for new messages
    const interval = setInterval(() => {
      if (activeTab === "chat") {
        fetchMessages();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab, projectId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [contactMessages]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/team/projects/${projectId}/detail`);
      setProject(res.data.project);
      setTeamContract(res.data.team_contract);
      setDailyProgress(res.data.daily_progress || []);
      setContactMessages(res.data.contact_messages || []);
      setApplication(res.data.application);
      setMemberShares(res.data.member_shares || []);
      setSharePerMember(res.data.share_per_member || 0);
      
      // Fetch completion status if project is in completion phase
      if (['pending_completion', 'completed'].includes(res.data.project?.status)) {
        fetchCompletionStatus();
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error(error.response?.data?.detail || "Failed to load project");
      navigate("/team-leader/my-projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/projects-v3/chat/project/${projectId}`);
      setContactMessages(res.data.messages || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  // Fetch completion status for this project
  const fetchCompletionStatus = async () => {
    try {
      const res = await axios.get(`${API}/projects-v3/completion-status/${projectId}`);
      setCompletionStatus(res.data);
    } catch (error) {
      console.error("Error fetching completion status:", error);
    }
  };

  // Check if current user has already signed completion
  const hasUserSignedCompletion = () => {
    if (!completionStatus?.signatures) return false;
    return completionStatus.signatures.some(
      sig => sig.user_id === user?.user_id && sig.signed
    );
  };

  // Sign completion
  const handleSignCompletion = async () => {
    if (!hasCompletionSignature) {
      toast.error(t('projectDetail.pleaseSign') || 'Vennligst signer først');
      return;
    }
    
    setSigningCompletion(true);
    try {
      // Get signature data from canvas
      const canvas = completionCanvasRef.current;
      const signatureData = canvas ? canvas.toDataURL('image/png') : null;
      
      const res = await axios.post(`${API}/projects-v3/sign-completion/${projectId}`, {
        signature_data: signatureData
      });
      
      toast.success(res.data.message);
      setShowCompletionSignModal(false);
      setHasCompletionSignature(false);
      fetchCompletionStatus();
      fetchProjectData();
    } catch (error) {
      console.error("Sign completion error:", error);
      toast.error(error.response?.data?.detail || 'Kunne ikke signere');
    } finally {
      setSigningCompletion(false);
    }
  };

  // Completion signature pad drawing functions
  const startCompletionDrawing = (e) => {
    const canvas = completionCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawingCompletion(true);
  };

  const drawCompletion = (e) => {
    if (!isDrawingCompletion) return;
    const canvas = completionCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasCompletionSignature(true);
  };

  const stopCompletionDrawing = () => {
    setIsDrawingCompletion(false);
  };

  const clearCompletionSignature = () => {
    const canvas = completionCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      setHasCompletionSignature(false);
    }
  };

  // Setup completion canvas
  useEffect(() => {
    if (showCompletionSignModal && completionCanvasRef.current) {
      const canvas = completionCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      setHasCompletionSignature(false);
    }
  }, [showCompletionSignModal]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;
    
    setSendingChat(true);
    try {
      await axios.post(`${API}/projects-v3/chat/project/${projectId}`, {
        message: chatMessage.trim()
      });
      setChatMessage("");
      fetchMessages();
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingChat(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingPhoto(true);
    
    for (const file of files) {
      try {
        // Get Cloudinary signature
        const sigRes = await axios.get(`${API}/cloudinary/signature?folder=projects&resource_type=image`);
        const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
        
        // Upload to Cloudinary
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", api_key);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        formData.append("folder", folder);
        
        const cloudinaryRes = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          { method: "POST", body: formData }
        );
        
        const cloudinaryData = await cloudinaryRes.json();
        
        if (cloudinaryData.secure_url) {
          setProgressPhotos(prev => [...prev, cloudinaryData.secure_url]);
          toast.success(`Photo uploaded: ${file.name}`);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadingPhoto(false);
  };

  const removePhoto = (index) => {
    setProgressPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitProgress = async (e) => {
    e.preventDefault();
    if (!progressDescription.trim()) {
      toast.error("Please add a description");
      return;
    }
    
    setSubmittingProgress(true);
    try {
      await axios.post(`${API}/team/projects/${projectId}/daily-progress`, {
        description: progressDescription,
        hours_worked: progressHours ? parseFloat(progressHours) : null,
        photos: progressPhotos,
        notes: progressNotes
      });
      
      // Reset form
      setProgressDescription("");
      setProgressHours("");
      setProgressNotes("");
      setProgressPhotos([]);
      
      // Refresh progress list
      const res = await axios.get(`${API}/team/projects/${projectId}/daily-progress`);
      setDailyProgress(res.data);
      
      toast.success("Progress update posted!");
    } catch (error) {
      toast.error("Failed to post progress");
    } finally {
      setSubmittingProgress(false);
    }
  };

  const openSignDialog = () => {
    setSignDialogOpen(true);
  };

  // Check if waiting time acknowledgement is required
  const hasWaitingTime = project?.waiting_time_terms && project?.waiting_time_terms?.enabled !== false;
  const waitingTimeAcknowledged = !hasWaitingTime || sjaAcknowledged.waitingTime;
  
  const allSjaAcknowledged = sjaAcknowledged.readHazards && sjaAcknowledged.understandPPE && sjaAcknowledged.acceptTerms && waitingTimeAcknowledged;

  const handleSignContractWithSignature = async (signatureImage) => {
    if (!teamContract?.contract_id) {
      toast.error("Contract not found");
      return;
    }
    
    setSigningContract(true);
    try {
      const res = await axios.post(`${API}/team/contracts/${teamContract.contract_id}/sign`, {
        signature: signatureText.trim(),
        signature_image: signatureImage,
        cede_percentage: cedePercentage
      });
      toast.success(res.data.message);
      
      if (res.data.all_signed) {
        toast.success("All team members signed! Project is now active.");
      }
      
      setSignDialogOpen(false);
      
      // Refresh project data
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign contract");
    } finally {
      setSigningContract(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title="Project Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout navItems={navItems} title="Project Details">
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-[#475569]" />
          <p className="text-[#94A3B8]">Project not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title={project.title}>
      <div className="space-y-6" data-testid="team-project-detail">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10" 
              onClick={() => navigate("/team-leader/my-projects")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{project.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-green-500/20 text-green-400">Active Project</Badge>
                <Badge className="capitalize bg-[#23F7DD]/20 text-[#23F7DD]">{project.project_type}</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Team Contract Warning */}
        {teamContract && !teamContract.all_signed && teamContract.signatures?.some(s => s.user_id === user?.user_id && !s.signed) && (
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
                <div className="flex-1">
                  <p className="text-yellow-400 font-medium">Contract Signature Required</p>
                  <p className="text-sm text-[#94A3B8]">Please sign the team contract to fully activate this project</p>
                </div>
                <Button 
                  onClick={openSignDialog}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Sign Contract
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project Completion Signature Required */}
        {project.status === 'pending_completion' && completionStatus && !hasUserSignedCompletion() && (
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-orange-400" />
                <div className="flex-1">
                  <p className="text-orange-400 font-medium">
                    {t('projectDetail.completionSignatureRequired') || 'Ferdigstillelse - Signatur Kreves'}
                  </p>
                  <p className="text-sm text-[#94A3B8]">
                    {t('projectDetail.clientMarkedComplete') || 'Klienten har markert prosjektet som ferdig. Vennligst signer for å bekrefte.'}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
                    <span>{t('projectDetail.signed') || 'Signert'}: {completionStatus.signed_count}/{completionStatus.total_signatures}</span>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowCompletionSignModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {t('projectDetail.signCompletion') || 'Signer Ferdigstillelse'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project Completed Banner */}
        {project.status === 'completed' && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                <div className="flex-1">
                  <p className="text-green-400 font-medium">
                    {t('projectDetail.projectCompleted') || 'Prosjekt Ferdigstilt!'}
                  </p>
                  <p className="text-sm text-[#94A3B8]">
                    {t('projectDetail.paymentProcessingSoon') || 'Alle har signert. Utbetaling vil bli behandlet innen 14 dager.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#0A0A0A] border border-white/10">
            <TabsTrigger value="details" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <FileText className="w-4 h-4 mr-2" />Details
            </TabsTrigger>
            <TabsTrigger value="progress" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <TrendingUp className="w-4 h-4 mr-2" />Daily Progress
            </TabsTrigger>
            <TabsTrigger value="chat" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <MessageSquare className="w-4 h-4 mr-2" />Chat with Client
            </TabsTrigger>
            <TabsTrigger value="contract" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <FileSignature className="w-4 h-4 mr-2" />Contract
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Project Info */}
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#23F7DD]" />
                    Project Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {project.description && (
                    <p className="text-[#94A3B8]">{project.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <MapPin className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-xs uppercase">Location</span>
                      </div>
                      <p className="text-white">{project.location_city}</p>
                      {project.location_address && (
                        <p className="text-sm text-[#94A3B8]">{project.location_address}</p>
                      )}
                    </div>
                    
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <Calendar className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-xs uppercase">Start Date</span>
                      </div>
                      <p className="text-white">
                        {project.start_date ? new Date(project.start_date).toLocaleDateString('nb-NO') : "Flexible"}
                      </p>
                    </div>
                    
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <Users className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-xs uppercase">Crew Size</span>
                      </div>
                      <p className="text-white">{project.crew_size || "N/A"} workers</p>
                    </div>
                    
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <Clock className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-xs uppercase">Duration</span>
                      </div>
                      <p className="text-white">{project.estimated_duration || project.duration || "N/A"} hours</p>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                      <Building2 className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-xs uppercase">Client</span>
                    </div>
                    <p className="text-white">{project.client_name || project.company_name || "Private Client"}</p>
                    {project.contact_person && (
                      <p className="text-sm text-[#94A3B8]">Contact: {project.contact_person}</p>
                    )}
                  </div>

                  {/* Project Images */}
                  {project.project_images?.length > 0 && (
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-2">
                        <Image className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-xs uppercase">Prosjektbilder</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {project.project_images.map((img, idx) => (
                          <a 
                            key={idx}
                            href={img.url || img}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded overflow-hidden hover:opacity-80 transition-opacity"
                          >
                            <img 
                              src={img.url || img} 
                              alt={img.name || `Bilde ${idx + 1}`}
                              className="w-full h-20 object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Project Documents */}
                  {(project.project_documents?.length > 0 || project.files?.length > 0 || project.attachments?.length > 0 || project.documents?.length > 0) && (
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-2">
                        <FileText className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-xs uppercase">Dokumenter</span>
                      </div>
                      <div className="space-y-2">
                        {(project.project_documents || project.files || project.attachments || project.documents || []).map((file, idx) => (
                          <a 
                            key={idx}
                            href={typeof file === 'string' ? file : file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 bg-white/5 rounded hover:bg-white/10 transition-colors"
                          >
                            <FileText className="w-4 h-4 text-blue-400" />
                            <span className="text-blue-400 text-sm hover:underline">
                              {typeof file === 'string' ? `Dokument ${idx + 1}` : (file.name || file.filename || `Dokument ${idx + 1}`)}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Financial Info & SJA */}
              <div className="space-y-6">
                {/* Team Payment Card */}
                <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#4F46E5]/10 border-[#23F7DD]/30">
                  <CardContent className="p-6">
                    <div className="text-center">
                      <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-1">Net to Team</p>
                      <p className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD]">
                        {(teamContract?.net_to_team || project?.net_to_team || 0).toLocaleString()} kr
                      </p>
                      <p className="text-xs text-gray-500 mt-1">(brutto - før skatt)</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Full SJA Section */}
                {project.sja_form && (
                  <Card className="bg-[#0A0A0A] border-orange-500/30">
                    <CardHeader>
                      <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                        <Shield className="w-5 h-5 text-orange-400" />
                        Safety Job Analysis (SJA)
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Important</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Job Description */}
                      {project.sja_form.job_description && (
                        <div className="p-3 bg-white/5 rounded-lg">
                          <p className="text-gray-500 text-sm mb-1">📋 Job Description</p>
                          <p className="text-white">{project.sja_form.job_description}</p>
                        </div>
                      )}

                      {/* Emergency Info Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {project.sja_form.emergency_contact && (
                          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-red-400 text-sm mb-1">📞 Emergency Contact</p>
                            <p className="text-white font-bold text-lg">{project.sja_form.emergency_contact}</p>
                          </div>
                        )}
                        {project.sja_form.nearest_hospital && (
                          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-red-400 text-sm mb-1">🏥 Nearest Hospital</p>
                            <p className="text-white font-semibold">{project.sja_form.nearest_hospital}</p>
                          </div>
                        )}
                        {project.sja_form.first_aid_location && (
                          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                            <p className="text-green-400 text-sm mb-1">🩹 First Aid Location</p>
                            <p className="text-white font-semibold">{project.sja_form.first_aid_location}</p>
                          </div>
                        )}
                      </div>

                      {/* Hazards */}
                      {project.sja_form.hazards?.length > 0 && (
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <p className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            ⚠️ Identified Hazards
                          </p>
                          <div className="space-y-3">
                            {project.sja_form.hazards.map((hazard, idx) => {
                              const hazardId = typeof hazard === 'string' ? hazard : (hazard.id || hazard.hazard || `hazard_${idx}`);
                              const hazardLabel = hazardId.replace(/_/g, ' ');
                              const hazardMeasures = typeof hazard === 'object' ? (hazard.measures || hazard.mitigation) : null;
                              const hazardResponsible = typeof hazard === 'object' ? hazard.responsible : null;
                              const hazardProbability = typeof hazard === 'object' ? (hazard.probability || hazard.risk_level) : null;
                              const hazardConsequence = typeof hazard === 'object' ? hazard.consequence : null;
                              
                              return (
                                <div key={idx} className="p-3 bg-white/5 rounded-lg border border-red-500/20">
                                  <p className="text-white font-semibold capitalize flex items-center gap-2">
                                    ⚠️ {hazardLabel}
                                  </p>
                                  {(hazardProbability || hazardConsequence) && (
                                    <div className="flex gap-3 mt-1 text-xs">
                                      {hazardProbability && (
                                        <span className="text-yellow-400 bg-yellow-500/20 px-2 py-0.5 rounded">
                                          📊 Risk: {hazardProbability}
                                        </span>
                                      )}
                                      {hazardConsequence && (
                                        <span className="text-orange-400 bg-orange-500/20 px-2 py-0.5 rounded">
                                          💥 Impact: {hazardConsequence}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {hazardMeasures && (
                                    <div className="mt-2 p-2 bg-green-500/10 rounded border-l-2 border-green-500">
                                      <p className="text-sm text-green-400 font-medium">✅ Prevention Measures:</p>
                                      <p className="text-gray-300 text-sm">{hazardMeasures}</p>
                                    </div>
                                  )}
                                  {hazardResponsible && (
                                    <p className="text-sm text-purple-400 mt-1">
                                      👤 Responsible: {hazardResponsible}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* PPE Required with Emojis */}
                      {project.sja_form.ppe_required?.length > 0 && (
                        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <p className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            🛡️ Required PPE
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {project.sja_form.ppe_required.map((ppe, idx) => {
                              const ppeEmojis = {
                                helmet: "⛑️", safety_shoes: "👢", hi_vis: "🦺", harness: "🪢",
                                gloves: "🧤", ear_protection: "🎧", safety_glasses: "🥽", dust_mask: "😷",
                                face_shield: "🛡️", knee_pads: "🦵", respirator: "😮‍💨"
                              };
                              const emoji = ppeEmojis[ppe] || "🔧";
                              
                              return (
                                <div key={idx} className="p-2 bg-white/5 rounded-lg flex items-center gap-2">
                                  <span className="text-xl">{emoji}</span>
                                  <span className="text-white text-sm capitalize">{ppe.replace(/_/g, ' ')}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Additional Measures */}
                      {project.sja_form.additional_measures && (
                        <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                          <p className="text-yellow-400 text-sm mb-1">📋 Additional Safety Measures</p>
                          <p className="text-gray-300">{project.sja_form.additional_measures}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Daily Progress Tab */}
          <TabsContent value="progress" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Post Progress Form */}
              <div className="lg:col-span-1">
                <Card className="bg-[#0A0A0A] border-[#23F7DD]/30 sticky top-6">
                  <CardHeader>
                    <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#23F7DD]" />
                      Post Update
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmitProgress} className="space-y-4">
                      <div>
                        <Label className="text-[#94A3B8]">What did you do today? *</Label>
                        <Textarea
                          value={progressDescription}
                          onChange={(e) => setProgressDescription(e.target.value)}
                          placeholder="Describe the work completed..."
                          className="mt-1 bg-black/50 border-white/10 text-white"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label className="text-[#94A3B8]">Hours Worked</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={progressHours}
                          onChange={(e) => setProgressHours(e.target.value)}
                          placeholder="e.g., 8"
                          className="mt-1 bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-[#94A3B8]">Additional Notes</Label>
                        <Input
                          value={progressNotes}
                          onChange={(e) => setProgressNotes(e.target.value)}
                          placeholder="Any issues or comments..."
                          className="mt-1 bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      
                      {/* Photo Upload */}
                      <div>
                        <Label className="text-[#94A3B8]">Photos</Label>
                        <div className="mt-1">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#23F7DD]/50 transition-colors">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handlePhotoUpload}
                              className="hidden"
                            />
                            <Camera className="w-5 h-5 text-[#94A3B8]" />
                            <span className="text-sm text-[#94A3B8]">
                              {uploadingPhoto ? "Uploading..." : "Add Photos"}
                            </span>
                          </label>
                        </div>
                        
                        {/* Photo Previews */}
                        {progressPhotos.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {progressPhotos.map((url, idx) => (
                              <div key={idx} className="relative group">
                                <img 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${url}`} 
                                  alt={`Photo ${idx + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePhoto(idx)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={submittingProgress || !progressDescription.trim()}
                        className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                      >
                        {submittingProgress ? "Posting..." : "Post Progress"}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Progress Timeline */}
              <div className="lg:col-span-2">
                <Card className="bg-[#0A0A0A] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white font-['Unbounded']">Progress Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dailyProgress.length === 0 ? (
                      <div className="text-center py-12 text-[#94A3B8]">
                        <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No progress updates yet</p>
                        <p className="text-sm">Post your first update to keep the client informed</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {dailyProgress.map((entry) => (
                          <div key={entry.progress_id} className="p-4 bg-white/5 rounded-lg border-l-4 border-[#23F7DD]">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="text-white font-medium">{entry.posted_by_name}</p>
                                <p className="text-xs text-[#94A3B8]">
                                  {new Date(entry.created_at).toLocaleString()}
                                </p>
                              </div>
                              {entry.hours_worked && (
                                <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">
                                  <Clock className="w-3 h-3 mr-1" />{entry.hours_worked}h
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-[#94A3B8]">{entry.description}</p>
                            
                            {entry.notes && (
                              <p className="text-sm text-[#94A3B8] mt-2 italic">Note: {entry.notes}</p>
                            )}
                            
                            {entry.photos?.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {entry.photos.map((url, idx) => {
                                  // Check if URL is absolute (starts with http) or relative
                                  const imageUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                                  return (
                                    <a 
                                      key={idx} 
                                      href={imageUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img 
                                        src={imageUrl}
                                        alt={`Progress ${idx + 1}`}
                                        className="w-24 h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                                      />
                                    </a>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat" className="mt-6">
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-indigo-400" />
                  Chat Team
                </CardTitle>
                <p className="text-gray-400 text-sm mt-1">
                  Chat med klient, holding, team leader og arbeidere
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Participants list */}
                <div className="flex flex-wrap gap-2 pb-3 border-b border-[#2a2a4a]">
                  <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">
                    <User className="w-3 h-3 mr-1" />
                    Klient
                  </Badge>
                  <Badge className="bg-purple-500/20 text-purple-400">
                    <Building2 className="w-3 h-3 mr-1" />
                    Holding
                  </Badge>
                  <Badge className="bg-orange-500/20 text-orange-400">
                    <User className="w-3 h-3 mr-1" />
                    Basleder
                  </Badge>
                  <Badge className="bg-blue-500/20 text-blue-400">
                    <Users className="w-3 h-3 mr-1" />
                    Arbeidere
                  </Badge>
                </div>

                {/* Messages */}
                <div className="h-80 overflow-y-auto space-y-3 p-3 bg-[#0f0f1a] rounded-lg">
                  {contactMessages.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-center">
                      <div>
                        <MessageSquare className="w-10 h-10 mx-auto text-gray-600 mb-2" />
                        <p className="text-gray-500 text-sm">Ingen meldinger ennå</p>
                        <p className="text-gray-600 text-xs mt-1">Start en samtale</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {contactMessages.map((msg, idx) => {
                        const isOwnMessage = msg.sender_id === user?.user_id;
                        const senderColors = {
                          'client': 'bg-[#23F7DD]/20 border-[#23F7DD]/30',
                          'client_admin': 'bg-[#23F7DD]/20 border-[#23F7DD]/30',
                          'holding_admin': 'bg-purple-500/20 border-purple-500/30',
                          'platform_admin': 'bg-purple-500/20 border-purple-500/30',
                          'team_leader': 'bg-orange-500/20 border-orange-500/30',
                          'worker': 'bg-blue-500/20 border-blue-500/30'
                        };
                        const textColors = {
                          'client': 'text-[#23F7DD]',
                          'client_admin': 'text-[#23F7DD]',
                          'holding_admin': 'text-purple-400',
                          'platform_admin': 'text-purple-400',
                          'team_leader': 'text-orange-400',
                          'worker': 'text-blue-400'
                        };
                        const roleLabels = {
                          'client': 'Klient',
                          'client_admin': 'Klient',
                          'holding_admin': 'Holding',
                          'platform_admin': 'Admin',
                          'team_leader': 'Basleder',
                          'worker': 'Arbeider'
                        };
                        const bgColor = senderColors[msg.sender_type] || 'bg-gray-500/20 border-gray-500/30';
                        const txtColor = textColors[msg.sender_type] || 'text-gray-400';
                        const roleLabel = roleLabels[msg.sender_type] || msg.sender_type;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                          >
                            <div className={`max-w-[80%] p-3 rounded-lg border ${bgColor}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium ${txtColor}`}>
                                  {isOwnMessage ? 'Deg' : msg.sender_name}
                                </span>
                                <Badge variant="outline" className={`text-[10px] px-1 py-0 ${txtColor} border-current`}>
                                  {roleLabel}
                                </Badge>
                              </div>
                              <p className="text-white text-sm">{msg.message}</p>
                              <p className="text-xs opacity-50 mt-1">
                                {new Date(msg.sent_at || msg.created_at).toLocaleString("no-NO", {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit"
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={chatEndRef} />
                    </>
                  )}
                </div>
                
                {/* Send Message Form */}
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Textarea
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="Skriv en melding..."
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white resize-none"
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                  <Button 
                    type="submit" 
                    disabled={sendingChat || !chatMessage.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contract Tab */}
          <TabsContent value="contract" className="mt-6">
            {teamContract ? (
              <Card className="bg-[#0A0A0A] border-cyan-500/30">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <FileSignature className="w-5 h-5 text-cyan-400" />
                    Team Contract Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Overall Status */}
                  <div className={`p-4 rounded-lg ${
                    teamContract.all_signed || teamContract.all_members_signed 
                      ? "bg-green-500/10 border border-green-500/30" 
                      : "bg-yellow-500/10 border border-yellow-500/30"
                  }`}>
                    <div className="flex items-center gap-2">
                      {teamContract.all_signed || teamContract.all_members_signed ? (
                        <><CheckCircle2 className="w-5 h-5 text-green-400" /><span className="text-green-400 font-medium">✅ All Signatures Complete - Project is Active!</span></>
                      ) : (
                        <><AlertTriangle className="w-5 h-5 text-yellow-400" /><span className="text-yellow-400 font-medium">⏳ Awaiting Signatures</span></>
                      )}
                    </div>
                  </div>
                  
                  {/* Financial Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-black/30 rounded-lg text-center">
                      <p className="text-xs text-[#94A3B8]">Net to Team</p>
                      <p className="text-xl font-bold text-[#23F7DD]">
                        {(teamContract.net_to_team || project?.net_to_team)?.toLocaleString()} kr
                      </p>
                    </div>
                    <div className="p-4 bg-black/30 rounded-lg text-center">
                      <p className="text-xs text-[#94A3B8]">Team Size</p>
                      <p className="text-xl font-bold text-white">
                        {1 + (teamContract.team_members?.length || application?.team_members?.length || 0)}
                      </p>
                    </div>
                  </div>

                  {/* Team Leader Signature - V3 format */}
                  <div className="space-y-2">
                    <p className="text-sm text-[#94A3B8] font-semibold">👤 Team Leader:</p>
                    {(() => {
                      const tlShare = memberShares.find(m => m.role === "Team Leader") || {};
                      return (
                        <div className={`flex items-center justify-between p-4 rounded-lg border ${
                          teamContract.team_signed 
                            ? "bg-green-500/10 border-green-500/30" 
                            : "bg-yellow-500/10 border-yellow-500/30"
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              teamContract.team_signed ? "bg-green-500/20" : "bg-yellow-500/20"
                            }`}>
                              {teamContract.team_signed ? (
                                <CheckCircle2 className="w-5 h-5 text-green-400" />
                              ) : (
                                <Clock className="w-5 h-5 text-yellow-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-semibold">
                                {teamContract.team_signed_by || teamContract.team_leader_name || "Team Leader"}
                              </p>
                              <p className="text-xs text-[#94A3B8]">Team Leader</p>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-4">
                            {/* Gross Amount */}
                            {tlShare.final_share && (
                              <div className="text-right">
                                <p className="text-xs text-[#94A3B8]">Brutto</p>
                                <p className="text-[#23F7DD] font-bold">{tlShare.final_share?.toLocaleString()} kr</p>
                                {tlShare.ceded_from_team > 0 && (
                                  <p className="text-xs text-green-400">+{tlShare.ceded_from_team?.toLocaleString()} kr fra team</p>
                                )}
                              </div>
                            )}
                            <div>
                              {teamContract.team_signed ? (
                                <>
                                  <Badge className="bg-green-500/20 text-green-400">✓ Signed</Badge>
                                  {teamContract.team_signed_at && (
                                    <p className="text-xs text-[#94A3B8] mt-1">
                                      {new Date(teamContract.team_signed_at).toLocaleDateString('nb-NO')}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <Badge className="bg-yellow-500/20 text-yellow-400">⏳ Pending</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Team Members Signatures - V3 format */}
                  {(teamContract.team_members?.length > 0 || application?.team_members?.length > 0) && (
                    <div className="space-y-2">
                      <p className="text-sm text-[#94A3B8] font-semibold">👥 Team Members:</p>
                      <div className="space-y-2">
                        {(teamContract.team_members || application?.team_members || []).map((member, idx) => {
                          const memberId = member.user_id || member.id;
                          // Check both contract.member_signatures AND project.worker_signatures
                          const memberSig = teamContract.member_signatures?.[memberId];
                          const workerSig = project?.worker_signatures?.find(s => s.user_id === memberId);
                          const hasSigned = !!memberSig || !!workerSig;
                          const signedAt = memberSig?.signed_at || workerSig?.signed_at;
                          
                          // Get member share info
                          const memberShare = memberShares.find(m => m.user_id === memberId) || {};
                          
                          return (
                            <div key={idx} className={`flex items-center justify-between p-4 rounded-lg border ${
                              hasSigned 
                                ? "bg-green-500/10 border-green-500/30" 
                                : "bg-blue-500/10 border-blue-500/30"
                            }`}>
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  hasSigned ? "bg-green-500/20" : "bg-blue-500/20"
                                }`}>
                                  {hasSigned ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                                  ) : (
                                    <Clock className="w-5 h-5 text-blue-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-white font-semibold">{member.name}</p>
                                  <p className="text-xs text-[#94A3B8]">
                                    {member.role || "Worker"} 
                                    {member.worker_code && ` • ${member.worker_code}`}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right flex items-center gap-4">
                                {/* Gross Amount */}
                                {memberShare.final_share && (
                                  <div className="text-right">
                                    <p className="text-xs text-[#94A3B8]">Brutto</p>
                                    <p className="text-[#23F7DD] font-bold">{memberShare.final_share?.toLocaleString()} kr</p>
                                    {memberShare.cede_percentage > 0 && (
                                      <p className="text-xs text-orange-400">-{memberShare.cede_percentage}% til TL</p>
                                    )}
                                  </div>
                                )}
                                <div>
                                  {hasSigned ? (
                                    <>
                                      <Badge className="bg-green-500/20 text-green-400">✓ Signed</Badge>
                                      {signedAt && (
                                        <p className="text-xs text-[#94A3B8] mt-1">
                                          {new Date(signedAt).toLocaleDateString('nb-NO')}
                                        </p>
                                      )}
                                    </>
                                  ) : (
                                    <Badge className="bg-blue-500/20 text-blue-400">⏳ Awaiting</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* V2 Signatures fallback */}
                  {teamContract.signatures && teamContract.signatures.length > 0 && !teamContract.team_signed && (
                    <div className="space-y-2">
                      <p className="text-sm text-[#94A3B8] mb-2">Team Member Signatures:</p>
                      <div className="space-y-2">
                        {teamContract.signatures.map((sig, idx) => (
                          <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                            sig.signed ? "bg-green-500/10 border border-green-500/30" : "bg-yellow-500/10 border border-yellow-500/30"
                          }`}>
                            <span className="text-white">{sig.name}</span>
                            <Badge className={sig.signed ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                              {sig.signed ? "✓ Signed" : "⏳ Pending"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sign Button - Show if current user needs to sign */}
                  {!teamContract.team_signed && user?.role === "team_leader" && (
                    <Button 
                      onClick={() => navigate(`/team-leader/sign-contract/${project.project_id}`)}
                      className="w-full bg-[#23F7DD] hover:bg-[#1de9cf] text-black font-bold py-6"
                    >
                      <FileSignature className="w-5 h-5 mr-2" />
                      Sign Team Contract
                    </Button>
                  )}

                  {/* Worker sign button */}
                  {teamContract.team_signed && user?.role === "worker" && 
                   !teamContract.member_signatures?.[user?.user_id] && 
                   !project?.worker_signatures?.some(s => s.user_id === user?.user_id) && (
                    <Button 
                      onClick={() => navigate(`/worker/sign-contract/${project.project_id}`)}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-6"
                    >
                      <FileSignature className="w-5 h-5 mr-2" />
                      Sign Your Contract
                    </Button>
                  )}

                  {/* Show success if already signed */}
                  {((teamContract.team_signed && user?.role === "team_leader") || 
                    (user?.role === "worker" && (
                      teamContract.member_signatures?.[user?.user_id] || 
                      project?.worker_signatures?.some(s => s.user_id === user?.user_id)
                    ))) && (
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-medium">✅ You have signed this contract</span>
                    </div>
                  )}
                  
                  {/* View Contract & Download Buttons */}
                  {teamContract.team_signed && (
                    <div className="flex gap-3 mt-4">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const route = user?.role === "worker" 
                            ? `/worker/contract/${project.project_id}`
                            : `/team-leader/contract/${project.project_id}`;
                          navigate(route);
                        }}
                        className="flex-1 border-white/20 text-white hover:bg-white/10"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Contract
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={async () => {
                          try {
                            const response = await axios.get(
                              `${API}/contracts-pdf/team/${project.project_id}`,
                              { responseType: 'blob' }
                            );
                            const blob = new Blob([response.data], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `team-contract-${project.project_id}.pdf`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                            toast.success("Contract downloaded!");
                          } catch (error) {
                            toast.error("Failed to download contract");
                          }
                        }}
                        className="flex-1 border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="p-12 text-center">
                  <FileSignature className="w-12 h-12 mx-auto mb-4 text-[#94A3B8] opacity-50" />
                  <p className="text-[#94A3B8]">No contract available yet</p>
                  <p className="text-xs text-[#475569] mt-2">Contract will be created when the team is selected</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Sign Contract Dialog */}
        <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-xl">Team Agreement Contract</DialogTitle>
            </DialogHeader>
            
            {project && teamContract && (
              <div className="space-y-6">
                {/* Contract Header */}
                <div className="p-4 bg-gradient-to-r from-[#23F7DD]/10 to-purple-500/10 rounded-lg border border-[#23F7DD]/30">
                  <h3 className="text-xl font-bold text-white mb-4">{project.title}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-[#64748B]">Team Payment</span>
                      <p className="text-[#23F7DD] font-bold text-lg">{teamContract.net_amount?.toLocaleString()} NOK</p>
                    </div>
                    <div>
                      <span className="text-[#64748B]">Location</span>
                      <p className="text-white">{project.location_city || "TBD"}</p>
                    </div>
                    <div>
                      <span className="text-[#64748B]">Start Date</span>
                      <p className="text-white">{project.start_date ? new Date(project.start_date).toLocaleDateString() : "TBD"}</p>
                    </div>
                    <div>
                      <span className="text-[#64748B]">Project Type</span>
                      <p className="text-white capitalize">{project.project_type}</p>
                    </div>
                  </div>
                </div>

                {/* Team Allocations */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#23F7DD]" />
                    Team Allocations
                  </h4>
                  
                  <div className="space-y-3">
                    {teamContract.signatures?.map((member, idx) => {
                      const isMe = member.user_id === user?.user_id;
                      const totalMembers = teamContract.signatures?.length || 1;
                      const allocationPercentage = (100 / totalMembers).toFixed(1);
                      const memberAmount = (teamContract.net_amount / totalMembers).toFixed(0);
                      
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${isMe ? 'bg-[#23F7DD]/10 border-[#23F7DD]/30' : 'bg-black/30 border-white/10'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                member.role === 'team_leader' ? 'bg-purple-500/20' : 'bg-[#23F7DD]/20'
                              }`}>
                                <span className={`text-sm font-bold ${
                                  member.role === 'team_leader' ? 'text-purple-400' : 'text-[#23F7DD]'
                                }`}>
                                  {member.role === 'team_leader' ? 'TL' : 'W'}
                                </span>
                              </div>
                              <div>
                                <p className="text-white font-medium">
                                  {member.name} {isMe && <span className="text-[#23F7DD]">(You)</span>}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-white font-bold">{allocationPercentage}%</p>
                                <p className="text-sm text-[#94A3B8]">{Number(memberAmount).toLocaleString()} NOK</p>
                              </div>
                              
                              {member.signed ? (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Signed
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Waiting Time Terms */}
                {project.waiting_time_terms && project.waiting_time_terms.enabled !== false && (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-400" />
                      Waiting Time Compensation
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {project.waiting_time_terms.max_hours_per_person && (
                        <div>
                          <p className="text-[#94A3B8]">Max Hours/Person</p>
                          <p className="text-white font-medium">{project.waiting_time_terms.max_hours_per_person} hours</p>
                        </div>
                      )}
                      {(project.waiting_time_terms.hourly_rate > 0 || project.waiting_time_terms.rate_per_hour > 0) && (
                        <div>
                          <p className="text-[#94A3B8]">Hourly Rate</p>
                          <p className="text-white font-medium">{project.waiting_time_terms.hourly_rate || project.waiting_time_terms.rate_per_hour} NOK</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 p-3 bg-black/30 rounded-lg">
                      <p className="text-xs text-[#94A3B8]">
                        <strong>Important:</strong> The waiting time compensation is <strong>already included</strong> in the total project price.
                      </p>
                    </div>
                    
                    {/* Waiting Time Acknowledgement Checkbox */}
                    <div 
                      className={`mt-4 flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                        sjaAcknowledged.waitingTime 
                          ? 'bg-green-500/10 border-green-500/30' 
                          : 'bg-black/30 border-white/10 hover:bg-white/5'
                      }`}
                      onClick={() => setSjaAcknowledged(prev => ({ ...prev, waitingTime: !prev.waitingTime }))}
                    >
                      <Checkbox
                        id="waitingTimeTop"
                        checked={sjaAcknowledged.waitingTime}
                        onCheckedChange={(checked) => setSjaAcknowledged(prev => ({ ...prev, waitingTime: checked }))}
                        className="border-orange-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <Label htmlFor="waitingTimeTop" className="text-white cursor-pointer text-sm">
                        I acknowledge and accept the waiting time compensation terms
                      </Label>
                    </div>
                  </div>
                )}

                {/* SJA Form - Required Reading */}
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    Safe Job Analysis (SJA) - MUST READ & ACKNOWLEDGE
                  </h4>
                  
                  {project.sja_form ? (
                    <div className="space-y-4">
                      {/* Job Description */}
                      {project.sja_form.job_description && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-1">Job Description:</p>
                          <p className="text-white">{project.sja_form.job_description}</p>
                        </div>
                      )}
                      
                      {/* Hazards with Checkboxes */}
                      {project.sja_form.hazards && Array.isArray(project.sja_form.hazards) && project.sja_form.hazards.filter(h => h && (h.hazard || h.description)).length > 0 && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Identified Hazards - Check each to confirm you understand:
                          </p>
                          <div className="space-y-3">
                            {project.sja_form.hazards.filter(h => h && (h.hazard || h.description)).map((h, idx) => (
                              <div 
                                key={idx} 
                                className={`p-3 rounded-lg border transition-all ${
                                  hazardAcknowledged[idx] 
                                    ? 'bg-green-500/10 border-green-500/30' 
                                    : 'bg-red-500/5 border-red-500/20'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <Checkbox
                                    id={`hazard-${idx}`}
                                    checked={hazardAcknowledged[idx] || false}
                                    onCheckedChange={(checked) => setHazardAcknowledged(prev => ({ ...prev, [idx]: checked }))}
                                    className="mt-1 border-red-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                  />
                                  <Label htmlFor={`hazard-${idx}`} className="cursor-pointer flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <AlertTriangle className={`w-4 h-4 ${hazardAcknowledged[idx] ? 'text-green-400' : 'text-yellow-400'}`} />
                                      <span className="text-white font-medium">{h.hazard || h.description || h}</span>
                                      {h.risk_level && (
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                          h.risk_level === 'red' ? 'bg-red-500/20 text-red-400' :
                                          h.risk_level === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                                          'bg-green-500/20 text-green-400'
                                        }`}>{h.risk_level === 'red' ? 'HIGH RISK' : h.risk_level === 'yellow' ? 'MEDIUM RISK' : 'LOW RISK'}</span>
                                      )}
                                    </div>
                                    {(h.mitigation || h.preventive_measures) && (
                                      <p className="text-sm text-[#94A3B8] ml-6">
                                        <span className="text-green-400">✓ Preventive measure:</span> {h.mitigation || h.preventive_measures}
                                      </p>
                                    )}
                                  </Label>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Hazards acknowledgement status */}
                          <div className="mt-3 flex items-center justify-between text-sm">
                            <span className="text-[#64748B]">
                              Acknowledged: {Object.values(hazardAcknowledged).filter(Boolean).length} / {project.sja_form.hazards.filter(h => h && (h.hazard || h.description)).length}
                            </span>
                            {Object.values(hazardAcknowledged).every(Boolean) && project.sja_form.hazards.filter(h => h && (h.hazard || h.description)).length > 0 && (
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle2 className="w-4 h-4" />
                                All hazards acknowledged
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* PPE Required */}
                      {project.sja_form.ppe_required && project.sja_form.ppe_required.length > 0 && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-2 flex items-center gap-2">
                            <HardHat className="w-4 h-4" />
                            Required PPE (Personal Protective Equipment):
                          </p>
                          {Array.isArray(project.sja_form.ppe_required) ? (
                            <div className="flex flex-wrap gap-2">
                              {project.sja_form.ppe_required.map((ppe, idx) => (
                                <span key={idx} className="px-3 py-1.5 bg-red-500/20 text-red-300 rounded-full text-sm font-medium capitalize">
                                  {ppe.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-white">{project.sja_form.ppe_required}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Emergency Contact */}
                      {project.sja_form.emergency_contact && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-1">Emergency Contact:</p>
                          <p className="text-white">{project.sja_form.emergency_contact}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-[#94A3B8]">Standard safety protocols apply. Contact project manager for specific requirements.</p>
                  )}
                </div>

                {/* Cede Percentage to TL (Optional) - Only for Workers */}
                {(() => {
                  if (!teamContract || !user) return null;
                  
                  // Check if current user is NOT the team leader
                  const isTeamLeader = teamContract.team_leader_id === user.user_id;
                  const hasMultipleMembers = teamContract.signatures?.length > 1;
                  
                  // Only show for workers when there are multiple members
                  if (!isTeamLeader && hasMultipleMembers) {
                    const totalMembers = teamContract.signatures?.length || 1;
                    const myAllocation = teamContract.net_amount / totalMembers;
                    
                    return (
                      <div className="p-4 bg-gradient-to-r from-[#23F7DD]/10 to-green-500/10 border border-[#23F7DD]/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-4">
                          <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                          <h4 className="text-white font-semibold">Team Leader Coordination Fee (Optional)</h4>
                        </div>
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-[#94A3B8]">Cede percentage to Team Leader</Label>
                          <span className="text-[#23F7DD] font-bold text-2xl">{cedePercentage}%</span>
                        </div>
                        <Slider
                          value={[cedePercentage]}
                          onValueChange={(value) => setCedePercentage(value[0])}
                          max={3}
                          step={0.5}
                          className="mb-3"
                        />
                        <div className="flex justify-between text-xs text-[#64748B]">
                          <span>0%</span>
                          <span>1%</span>
                          <span>2%</span>
                          <span>3%</span>
                        </div>
                        <p className="text-sm text-[#94A3B8] mt-3">
                          You can optionally cede up to 3% of your share to the Team Leader as a coordination fee.
                        </p>
                        {cedePercentage > 0 && (
                          <div className="mt-3 p-2 bg-[#23F7DD]/20 rounded text-center">
                            <span className="text-[#23F7DD] font-medium">
                              Amount to cede: {((myAllocation || 0) * cedePercentage / 100).toLocaleString(undefined, {maximumFractionDigits: 0})} NOK
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Sign Agreement Section - Part of scroll */}
                <div className="pt-4 border-t border-white/10 mt-4">
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-4">
                    <h4 className="text-white font-semibold flex items-center gap-2">
                      <Signature className="w-5 h-5 text-purple-400" />
                      Sign Agreement
                    </h4>
                  
                  {/* Final Acknowledgements - 3 checkboxes only */}
                  <div className="space-y-3">
                    <div 
                      className={`flex items-start gap-3 p-3 rounded-lg transition-all cursor-pointer ${
                        sjaAcknowledged.readHazards 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-black/30 border border-white/10 hover:bg-white/5'
                      }`}
                      onClick={() => setSjaAcknowledged(prev => ({ ...prev, readHazards: !prev.readHazards }))}
                    >
                      <Checkbox
                        id="readHazards"
                        checked={sjaAcknowledged.readHazards}
                        onCheckedChange={(checked) => setSjaAcknowledged(prev => ({ ...prev, readHazards: checked }))}
                        className="mt-0.5 border-red-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <Label htmlFor="readHazards" className="text-white cursor-pointer text-sm leading-relaxed">
                        I have read and understood all <span className="text-red-400 font-semibold">identified hazards</span> and preventive measures.
                      </Label>
                    </div>
                    
                    <div 
                      className={`flex items-start gap-3 p-3 rounded-lg transition-all cursor-pointer ${
                        sjaAcknowledged.understandPPE 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-black/30 border border-white/10 hover:bg-white/5'
                      }`}
                      onClick={() => setSjaAcknowledged(prev => ({ ...prev, understandPPE: !prev.understandPPE }))}
                    >
                      <Checkbox
                        id="understandPPE"
                        checked={sjaAcknowledged.understandPPE}
                        onCheckedChange={(checked) => setSjaAcknowledged(prev => ({ ...prev, understandPPE: checked }))}
                        className="mt-0.5 border-blue-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <Label htmlFor="understandPPE" className="text-white cursor-pointer text-sm leading-relaxed">
                        I will comply with all <span className="text-blue-400 font-semibold">PPE requirements</span> specified in the SJA.
                      </Label>
                    </div>
                    
                    <div 
                      className={`flex items-start gap-3 p-3 rounded-lg transition-all cursor-pointer ${
                        sjaAcknowledged.acceptTerms 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-black/30 border border-white/10 hover:bg-white/5'
                      }`}
                      onClick={() => setSjaAcknowledged(prev => ({ ...prev, acceptTerms: !prev.acceptTerms }))}
                    >
                      <Checkbox
                        id="acceptTerms"
                        checked={sjaAcknowledged.acceptTerms}
                        onCheckedChange={(checked) => setSjaAcknowledged(prev => ({ ...prev, acceptTerms: checked }))}
                        className="mt-0.5 border-green-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <Label htmlFor="acceptTerms" className="text-white cursor-pointer text-sm leading-relaxed">
                        I accept the <span className="text-green-400 font-semibold">contract terms</span>, including my allocation of <span className="text-[#23F7DD] font-bold">{((100 / (teamContract.signatures?.length || 1))).toFixed(1)}%</span> ({(teamContract.net_amount / (teamContract.signatures?.length || 1)).toLocaleString(undefined, {maximumFractionDigits: 0})} NOK).
                      </Label>
                    </div>
                  </div>
                  
                  {/* Signature Name - Auto-filled with logged in user */}
                  <div className="space-y-2 pt-4 border-t border-white/10">
                    <Label className="text-white font-medium">Your Name (auto-filled)</Label>
                    <Input
                      value={signatureText}
                      onChange={(e) => setSignatureText(e.target.value)}
                      placeholder="Your name"
                      className="bg-black/50 border-white/10 text-white focus:border-purple-400 text-lg"
                      readOnly
                    />
                  </div>
                  
                  {/* Sign Button */}
                  <Button
                    onClick={() => setSignaturePadOpen(true)}
                    disabled={!allSjaAcknowledged}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg font-bold disabled:opacity-50"
                  >
                    <Signature className="w-5 h-5 mr-2" />
                    Sign with Digital Signature
                  </Button>
                  
                  {!allSjaAcknowledged && (
                    <p className="text-xs text-yellow-400 text-center">Please check all boxes above to enable signing</p>
                  )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Digital Signature Pad Dialog */}
        <Dialog open={signaturePadOpen} onOpenChange={setSignaturePadOpen}>
          <DialogContent className="bg-[#0A0A0A] border-purple-500/30 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                <Signature className="w-6 h-6 text-purple-400" />
                Digital Signature
              </DialogTitle>
              <DialogDescription className="text-[#94A3B8]">
                Draw your signature below to sign the contract
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Signature Canvas */}
              <div className="relative">
                <canvas 
                  ref={canvasRef}
                  width={400}
                  height={200}
                  className="w-full bg-white rounded-lg cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={stopDrawing}
                />
                {!hasSignature && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-gray-400 text-sm">Sign here with mouse or finger</p>
                  </div>
                )}
              </div>
              
              {/* Clear Button */}
              <Button
                variant="outline"
                onClick={clearSignature}
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Clear Signature
              </Button>
              
              {/* Confirm Sign Button */}
              <Button
                onClick={handleConfirmSignature}
                disabled={!hasSignature || signingContract}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg font-bold disabled:opacity-50"
              >
                {signingContract ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Confirming Signature...
                  </span>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Confirm & Sign Contract
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Completion Signature Modal */}
        <Dialog open={showCompletionSignModal} onOpenChange={setShowCompletionSignModal}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <CheckCircle2 className="w-5 h-5 text-orange-400" />
                {t('projectDetail.signCompletion') || 'Signer Ferdigstillelse'}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                {t('projectDetail.signCompletionDesc') || 'Bekreft at prosjektet er ferdig og signer for å godkjenne utbetaling'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Completion info */}
              {completionStatus && (
                <div className="p-4 bg-white/5 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{t('common.project') || 'Prosjekt'}</span>
                    <span className="text-white font-medium">{project?.title}</span>
                  </div>
                  {completionStatus.completion_note && (
                    <div>
                      <span className="text-gray-400 text-sm">{t('projectDetail.clientNote') || 'Klientens kommentar'}:</span>
                      <p className="text-white text-sm mt-1">{completionStatus.completion_note}</p>
                    </div>
                  )}
                  {completionStatus.completion_files?.length > 0 && (
                    <div>
                      <span className="text-gray-400 text-sm">{t('projectDetail.completionFiles') || 'Vedlagte filer'}:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {completionStatus.completion_files.map((file, idx) => (
                          <a 
                            key={idx}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#23F7DD] text-sm hover:underline flex items-center gap-1"
                          >
                            <Image className="w-4 h-4" />
                            {file.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Signature pad */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">
                  {t('projectDetail.drawSignature') || 'Tegn din signatur'}
                </label>
                <div className="border border-white/20 rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={completionCanvasRef}
                    width={400}
                    height={150}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startCompletionDrawing}
                    onMouseMove={drawCompletion}
                    onMouseUp={stopCompletionDrawing}
                    onMouseLeave={stopCompletionDrawing}
                    onTouchStart={startCompletionDrawing}
                    onTouchMove={drawCompletion}
                    onTouchEnd={stopCompletionDrawing}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearCompletionSignature}
                  className="mt-2 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('common.clear') || 'Fjern'}
                </Button>
              </div>

              {/* Confirmation text */}
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-orange-400 text-sm">
                  {t('projectDetail.completionConfirmText') || 'Ved å signere bekrefter du at prosjektet er fullført og at du godkjenner utbetaling iht. avtale.'}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCompletionSignModal(false);
                  setHasCompletionSignature(false);
                }}
                className="text-gray-400"
              >
                {t('common.cancel') || 'Avbryt'}
              </Button>
              <Button
                onClick={handleSignCompletion}
                disabled={signingCompletion || !hasCompletionSignature}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {signingCompletion 
                  ? (t('common.loading') || 'Venter...') 
                  : (t('projectDetail.confirmSignature') || 'Bekreft Signatur')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
